/**
 * Sol Room Theme - Customized Junto theme for The Sol Room
 * This theme extends the base Junto theme with Sol Room specific branding
 */

import { baseJuntoTheme } from './base.js';

export const solRoomTheme = {
  ...baseJuntoTheme,
  colors: {
    primary: {
      // Main background and text colors used throughout the app
      background: '#F6F4F3', // Used for: page backgrounds, footer, modal hover states
      text: '#413a38',       // Used for: all body text
      buttons: '#A18680',    // Used for: buttons (on hover)
      ui_dark: '#413a38',    // Used for: UI elements like borders, icons, logos
    },
    accent: {
      // Accent colors for interactive elements and highlights
      // rose-beige color scheme
      main: '#BFA19B',     // Used for: buttons, links, borders, hover states, text highlights
      bright: '#D8C2BD',   // Used for: decorative gradients (Hero component)
      deep: '#A18680',     // Used for: button hover states (darker variant)
    },
    neutral: {
      // Neutral scale for backgrounds and text on dark backgrounds
      lightest: '#FBFBFB', // Used for: text on dark backgrounds, modal backgrounds, light sections
      light: '#E8EAE6',    // Used for: section backgrounds (Terms, Privacy, Team cards)
      medium: '#C8CCC5',   // Used for: section backgrounds (About page)
      dark: '#80857B',     // Used for: section backgrounds (About page)
    },
  },
  shadows: {
    // Box shadows and overlays used throughout the app
    light: 'rgba(0, 0, 0, 0.05)',      // Used for: subtle borders
    medium: 'rgba(0, 0, 0, 0.1)',      // Used for: cards, borders, subtle shadows
    strong: 'rgba(0, 0, 0, 0.15)',     // Used for: dropdown shadows, hover states
    dark: 'rgba(0, 0, 0, 0.2)',        // Used for: modal shadows
    darker: 'rgba(0, 0, 0, 0.3)',      // Used for: strong shadows
    overlay: {
      // Overlay backgrounds for modals and hero sections
      light: 'rgba(0, 0, 0, 0.3)',     // Used for: hero section overlays
      medium: 'rgba(0, 0, 0, 0.4)',    // Used for: banner overlays
      dark: 'rgba(0, 0, 0, 0.6)',      // Used for: modal overlays
      darkest: 'rgba(0, 0, 0, 0.7)',   // Used for: team modal overlay
    },
    // Special cases
    navBackground: 'rgba(246, 244, 243, 0.95)', // Transparent version of primary.background
    accentShadow: 'rgba(199, 155, 163, 0.15)',  // Transparent version of accent.main
  },
  typography: {
    primary: {
      fontFamily: "'Jacques Francois', serif",
      heroTitle: {
        fontSize: 'clamp(3rem, 10vw, 6rem)',
        fontWeight: '400',
        lineHeight: '1.1',
      },
      heading: {
        fontSize: 'clamp(2rem, 5vw, 3rem)',
        fontWeight: '400',
        lineHeight: '1.2',
      },
      largeHeading: {
        fontSize: 'clamp(3.5rem, 8vw, 4.5rem)',
        fontWeight: '400',
        lineHeight: '1.1',
      },
      mediumHeading: {
        fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
        fontWeight: '400',
        lineHeight: '1.2',
      },
      smallHeading: {
        fontSize: 'clamp(2rem, 4vw, 2.8rem)',
        fontWeight: '400',
        lineHeight: '1.2',
      },
    },
    secondary: {
      fontFamily: "'Poppins', sans-serif",
      body: {
        fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
        fontWeight: '400',
        lineHeight: '1.6',
      },
      button: {
        fontSize: '0.9rem',
        fontWeight: '500',
      },
      navigation: {
        fontSize: '0.9rem',
        fontWeight: '400',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      },
    },
    button: {
      fontFamily: "'Jacques Francois', serif",
      fontSize: 'calc(0.9rem * 1.25)',
      fontWeight: '300',
      letterSpacing: '0.02em',
      textTransform: 'none',
    },
    navigation: {
      fontFamily: "'Jacques Francois', serif",
      fontSize: 'calc(0.9rem * 1.25)',
      fontWeight: '300',
      letterSpacing: '0.02em',
      textTransform: 'none',
    },
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '32px',
    xl: '64px',
    xxl: '128px',
    layoutGutter: 'clamp(1rem, 5vw, 3rem)',
    sectionPadding: {
      vertical: 'clamp(4rem, 10vh, 8rem)',
      horizontal: 'clamp(1rem, 5vw, 3rem)',
    },
    section: {
      standard: '120px 0',
      mobile: '60px 0',
    },
    grid: {
      standard: '120px',
      mobile: '60px',
    },
    navigation: {
      height: '123px',
      mobileHeight: '80px',
    },
  },
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1440px',
  },
  transitions: {
    default: 'all 0.3s ease',
    slow: 'all 0.6s ease',
  },
  animations: {
    // Animation durations for scroll animations
    fast: 0.6,        // Fast animations
    medium: 0.8,      // Medium animations  
    slow: 1.0,        // Slow animations
    // Stagger delays
    staggerFast: 0.2,    // Quick stagger between elements
    staggerMedium: 0.3,  // Medium stagger
    staggerSlow: 0.4,    // Slow stagger
    // Movement distances
    moveSmall: 20,    // Small y movement
    moveMedium: 30,   // Medium y movement
    moveLarge: 50,    // Large y movement
    // Scale values
    scaleSmall: 0.9,  // Small scale effect
    scaleMedium: 0.8, // Medium scale effect
    // Gallery timing
    imageRotationInterval: 2500, // milliseconds between image changes
  },
  // Reusable CSS mixins
  mixins: {
    // Hover underline effect used across navigation and movement selectors
    hoverUnderline: (color = 'currentColor', thickness = '1px') => `
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: ${thickness};
        background-color: ${color};
        transition: width 0.3s ease;
      }
      
      &:hover::after {
        width: 100%;
      }
    `,
  },
};